import { useTranslation } from 'react-i18next'
import { ExternalLink, Navigation } from 'lucide-react'
import { CopyButton } from '@/components/ui/copy-button'
import { PlusCode } from '@/components/maps/PlusCode'
import { cn } from '@/lib/utils'

// #247 — the one location block every map surface shows: Google address (when the camera
// has one), Plus Code, coordinates, copy buttons, and links to Google Maps and to directions.

export const googleMapsUrl = (lat: number, lng: number) => `https://www.google.com/maps?q=${lat},${lng}`
export const directionsUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`

const linkClass =
  'inline-flex items-center gap-1 rounded-sm font-medium text-primary hover:text-primary/80 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
const labelClass = 'shrink-0 text-[10px] font-semibold uppercase tracking-wide opacity-70'

export function LocationDetails({
  lat,
  lng,
  address,
  openInMaps = true,
  className,
}: {
  lat: number
  lng: number
  address?: string | null
  /** Off where the surface already has its own "Open in Google Maps" button. */
  openInMaps?: boolean
  className?: string
}) {
  const { t } = useTranslation('common')
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  const coords = `${lat.toFixed(6)}, ${lng.toFixed(6)}`

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5 text-xs text-muted-foreground', className)}>
      {address && (
        <div className="flex items-start gap-1.5">
          <span className={cn(labelClass, 'mt-px')}>{t('address')}</span>
          <span className="min-w-0 break-words text-foreground/90">{address}</span>
          <CopyButton value={address} label={t('address')} />
        </div>
      )}
      <PlusCode lat={lat} lng={lng} />
      <div className="flex items-center gap-1.5">
        <span className={labelClass}>{t('coordinates')}</span>
        <span className="font-mono">{coords}</span>
        <CopyButton value={coords} label={t('coordinates')} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5">
        {openInMaps && (
          <a href={googleMapsUrl(lat, lng)} target="_blank" rel="noopener noreferrer" className={linkClass}>
            <ExternalLink className="h-3 w-3" aria-hidden />
            {t('openInGoogleMaps')}
          </a>
        )}
        <a href={directionsUrl(lat, lng)} target="_blank" rel="noopener noreferrer" className={linkClass}>
          <Navigation className="h-3 w-3" aria-hidden />
          {t('directions')}
        </a>
      </div>
    </div>
  )
}
